import { Markdown } from "@/components/ai/chat/Markdown";
import type { AgentMessage } from "@/hooks/use-stream-conversation";
import { ANIMATED_KEY_PREFIX } from "@/hooks/use-welcome-anchors";
import { useEffect, useRef, useState, type FC } from "react";

function getAnimatedLength(messageId: number): number {
    try {
        return Number(localStorage.getItem(`${ANIMATED_KEY_PREFIX}${messageId}`)) || 0;
    } catch {
        return 0;
    }
}

function setAnimatedLength(messageId: number, length: number) {
    try {
        localStorage.setItem(`${ANIMATED_KEY_PREFIX}${messageId}`, String(length));
    } catch {
        // localStorage may be unavailable
    }
}

/**
 * Renders an injected (artificial) agent message with word-by-word
 * streaming animation across all text fragments. Tracks what has
 * already been animated in localStorage so it doesn't replay on refresh.
 */
export const InjectedMessageBubble: FC<{ message: AgentMessage }> = ({ message }) => {
    const content = message.fragments.reduce(
        (acc, f) => (f.type === "text" ? (acc ? `${acc}\n\n${f.content}` : f.content) : acc),
        "",
    );

    const alreadyAnimatedLength = useRef(getAnimatedLength(message.id));
    const [displayedText, setDisplayedText] = useState(() => content.slice(0, alreadyAnimatedLength.current));
    const prevContentRef = useRef(content.slice(0, alreadyAnimatedLength.current));

    useEffect(() => {
        const prevContent = prevContentRef.current;
        if (content === prevContent) {
            return;
        }

        const newPart = content.slice(prevContent.length);
        prevContentRef.current = content;

        if (!newPart) {
            return;
        }

        const words = newPart.split(/(\s+)/);
        let index = 0;
        let accumulated = prevContent;

        const intervalId = setInterval(() => {
            if (index >= words.length) {
                clearInterval(intervalId);
                setAnimatedLength(message.id, content.length);
                return;
            }
            accumulated += words[index];
            index++;
            setDisplayedText(accumulated);
        }, 20);

        return () => {
            // Persist progress so far on cleanup
            setAnimatedLength(message.id, accumulated.length);
            clearInterval(intervalId);
            prevContentRef.current = accumulated;
        };
    }, [content, message.id]);

    const isComplete = displayedText === content;

    return (
        <div className="flex w-full flex-col gap-2 p-0 text-base">
            <div data-completed={isComplete}>
                <Markdown content={displayedText || "\u00A0"} />
            </div>
        </div>
    );
};
